import React, { useState, useEffect, useRef, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBookOpen,
  faChevronDown,
  faChevronUp,
  faCommentsAlt,
  faChevronLeft,
} from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { useSelector } from 'react-redux'
import { ICourseModel } from 'models/course.model'
import { IAssignedLessonModel } from 'models/assignedLesson.model'
import { AssignedLessonStateEnum } from 'enum'
import { IMainState } from 'store/states'
import { IEvaluationQuestion } from 'models/evaluation.model'
import { ILessonModel } from 'models/lesson.model'
import { useCourse } from 'contexts'
import LessonNavigation from './components/lesson-navigation/LessonNavigation'
import styles from './CourseSidebar.module.scss'

export interface Props {
  course: ICourseModel
  lessons: ILessonModel[]
  courseProgress: IAssignedLessonModel[]
  currentLesson: ILessonModel
  handleGoToLesson: (lesson: ILessonModel, lessonIndex: number) => void
  showChat?: any
  chatActive: boolean
  newMessages: number
  hideResume?: boolean
  onEvaluation?: boolean
  setOnEvaluation?: any
  evaluationData?: IEvaluationQuestion
  onIncompleteMultimedia?(): void
  onIncompleteEvaluations?(): void
  onIncompleteTasks?(): void
}
interface evaluationData {
  name: string
  maxRetries: number
  usedTries?: number
}

const ALLOW_SKIP_TO_NEXT_LESSON = false

const CourseSidebard: React.FC<Props> = ({
  course,
  lessons,
  courseProgress,
  currentLesson,
  handleGoToLesson,
  showChat,
  chatActive,
  newMessages,
  hideResume = false,
  onEvaluation = false,
  setOnEvaluation = undefined,
  evaluationData = undefined,
  onIncompleteMultimedia,
  onIncompleteEvaluations,
  onIncompleteTasks,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const history = useHistory()
  const { permission } = useSelector(({ user }: IMainState) => user)
  const [evaluation, setEvaluation] = useState({} as evaluationData)
  const lessonsCountRef = useRef(0)
  const [lessonsContainerHeight, setLessonsContainerHeight] = useState(141)
  const courseProgressByLesson = useMemo(
    () =>
      new Map(
        courseProgress.map((assignedLesson) => [
          assignedLesson.lessonId,
          assignedLesson,
        ])
      ),
    [courseProgress]
  )

  useEffect(() => {
    if (lessons) lessonsCountRef.current = lessons.length
  }, [lessons])

  useEffect(() => {
    let newHeight = 141
    if (!hideResume) newHeight += 38
    if (!permission.admin && !permission.superadmin && !permission.moderator)
      newHeight += 38
    setLessonsContainerHeight(newHeight)
  }, [
    hideResume,
    permission.admin,
    permission.superadmin,
    permission.moderator,
  ])

  const handleClick = (lesson: ILessonModel, index: number) => {
    setOpen(false)
    handleGoToLesson(lesson, index)
    showChat?.(false)
  }

  useEffect(() => {
    if (evaluationData && evaluationData.id) {
      const {
        name,
        config,
        results: { attemps },
      } = evaluationData
      const { maxRetries = 3 } = { ...config }
      if (maxRetries || name)
        setEvaluation({ name, maxRetries, usedTries: attemps + 1 })
    }
  }, [evaluationData])

  const {
    previewMode,
    multimediaProgress: { finished },
    canAnswer,
    validations: {
      lesson: { everyEvaluationsHasResponse, everyTasksHasResponse },
    },
  } = useCourse()

  const validateLessons = !previewMode && canAnswer

  return (
    <div>
      <div className={`${styles.sidebarCollapseTrigger} ${styles.onlyMobile}`}>
        <div
          className={`${styles.sidebarButton} d-flex align-items-center px-4`}
          onClick={() =>
            onEvaluation ? setOnEvaluation(false) : setOpen(!open)
          }
        >
          <span className="p-2">
            {t(`components.courseSidebar.${onEvaluation ? 'back' : 'lessons'}`)}
          </span>
          <FontAwesomeIcon
            icon={
              onEvaluation ? faChevronLeft : open ? faChevronUp : faChevronDown
            }
          />
        </div>
      </div>
      <div
        className={`${styles.sidebarContainer} ${styles.onlyDesktop} ${
          open && styles.open
        }`}
      >
        <div className={styles.sidebarContent}>
          <div>
            <h3 className={styles.courseTitle}>{course.name}</h3>
            <p className={styles.lessonCounter}>
              {!onEvaluation ? (
                <>
                  {lessons && lessons.length}{' '}
                  {t('components.courseSidebar.lessonsCount')}
                </>
              ) : (
                <>
                  {evaluation.name}
                  <br />
                  {t('components.courseSidebar.remainingAttempts', {
                    firstNumber: evaluation.usedTries,
                    secondNumber: evaluation.maxRetries,
                  })}
                </>
              )}
            </p>
          </div>
          {!onEvaluation ? (
            <>
              <div className={styles.links}>
                {!hideResume ? (
                  <a
                    onClick={() => history.push(`/courses/${course.id}`)}
                    className={`${styles.navbarButton} `}
                  >
                    <FontAwesomeIcon icon={faBookOpen} />
                    <span className={styles.navbarButtonText}>
                      {t('components.courseSidebar.backToResume')}
                    </span>
                  </a>
                ) : (
                  // Offset
                  <div className="mb-4" />
                )}
                {/* Chat */}
                {!permission.admin &&
                  !permission.superadmin &&
                  !permission.moderator && (
                    <div className="mt-2">
                      <a
                        onClick={() => showChat(true)}
                        className={`${styles.navbarButton} ${
                          chatActive ? styles.chatActive : ''
                        } position-relative`}
                      >
                        {/* Notification dot :) */}
                        <div
                          className={newMessages > 0 ? styles.newMessage : ''}
                        />
                        <FontAwesomeIcon icon={faCommentsAlt} />
                        <span className={styles.navbarButtonText}>
                          {t('components.courseSidebar.messages')}
                        </span>
                      </a>
                    </div>
                  )}
              </div>
              <div
                id={'lessonsContainer'}
                className={styles.lessonsContainer}
                style={{ height: `calc(100% - ${lessonsContainerHeight}px)` }}
              >
                {lessons?.map((lesson, index) => {
                  const isNextLesson = lesson.order > currentLesson.order
                  const lessonId = lesson.id || 0
                  const previousLessons = lessons.slice(0, index)
                  const previousFinished = previousLessons.every(({ id }) => {
                    const assignedLesson = courseProgressByLesson.get(id || 0)
                    return (
                      assignedLesson?.state === AssignedLessonStateEnum.Finished
                    )
                  })
                  return (
                    <a
                      id={`row-${index}`}
                      key={index}
                      className={`d-block ${styles.lesson} ${
                        validateLessons &&
                        isNextLesson &&
                        !previousFinished &&
                        !ALLOW_SKIP_TO_NEXT_LESSON
                          ? ` ${styles.disabled}`
                          : ''
                      }`}
                      onClick={() => {
                        if (validateLessons) {
                          if (isNextLesson) {
                            if (!ALLOW_SKIP_TO_NEXT_LESSON && !previousFinished)
                              return
                            if (!finished) return onIncompleteMultimedia?.()
                            if (!everyTasksHasResponse)
                              return onIncompleteTasks?.()
                            if (!everyEvaluationsHasResponse)
                              return onIncompleteEvaluations?.()
                          }
                        }
                        handleClick(lesson, index)
                      }}
                    >
                      {index + 1 < lessons.length && (
                        <div className={styles.lessonSeparator} />
                      )}
                      <LessonNavigation
                        id={lessonId}
                        lessonName={lesson.name}
                        index={index}
                        lessonState={courseProgressByLesson.get(lessonId)}
                        currentLesson={currentLesson}
                      />
                    </a>
                  )
                })}
              </div>
              <div className={styles.offset}> </div>
            </>
          ) : (
            <a
              onClick={() => setOnEvaluation(false)}
              className={`${styles.navbarButton} py-2 ${styles.backButton}`}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              <span className={styles.navbarButtonText}>
                {t('components.courseSidebar.back')}
              </span>
            </a>
          )}
        </div>
        {!onEvaluation && <div onClick={() => setOpen(false)} />}
      </div>
    </div>
  )
}

export default CourseSidebard
